// API Configuration for different environments
const isDevelopment = process.env.NODE_ENV === 'development';

const defaultProdHttpOrigin = '';

const defaultProdWsOrigin = '';

// Check for both variable names for backward compatibility
// REACT_APP_API_URL takes precedence over REACT_APP_API_BASE_URL
export const API_BASE_URL = process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  (isDevelopment ? 'http://localhost:8000' : defaultProdHttpOrigin);

export const WS_BASE_URL = process.env.REACT_APP_WS_URL ||
  (isDevelopment ? 'ws://localhost:8000' : defaultProdWsOrigin);

export const API_ENDPOINTS = {
  // Preprocessing
  preprocessing: `${API_BASE_URL}/api/preprocessing`,
  batchPreprocessing: `${API_BASE_URL}/api/batch-preprocessing`,

  // Processing
  processingStart: `${API_BASE_URL}/api/processing/start`,
  processingPause: (sessionId: string) => `${API_BASE_URL}/api/processing/pause/${sessionId}`,
  processingResume: (sessionId: string) => `${API_BASE_URL}/api/processing/resume/${sessionId}`,
  processingStop: (sessionId: string) => `${API_BASE_URL}/api/processing/stop/${sessionId}`,

  // Batch Processing
  batchProcessingStart: `${API_BASE_URL}/api/batch-processing/start`,

  // Edit Sessions
  editSession: `${API_BASE_URL}/api/edit-session/create`,
  editSessionInfo: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}`,
  editSessionLock: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/lock`,
  editSessionUnlock: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/unlock`,
  editSessionSave: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/save`,
  editSessionDiscard: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/discard`,
  editSessionMerge: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/merge-units`,
  editSessionSplit: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/split-unit`,
  editSessionMergeByUuid: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/merge-by-uuid`,
  editSessionSplitByUuid: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/split-by-uuid`,
  editSessionDelete: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/delete-unit`,
  editSessionCrossChunkMerge: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/merge-cross-chunk`,
  editSessionUpdateUnitText: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/update-unit-text`,
  editSessionAiRefine: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/ai-refine`,
  editSessionAiRefineWithGroundTruth: (sessionId: string) => `${API_BASE_URL}/api/edit-session/${sessionId}/ai-refine-with-ground-truth`,
  editSessionReadiness: `${API_BASE_URL}/api/edit-session/readiness`,

  // Session Export/Import
  editSessionExport: (sessionId: string, includeMetadata: boolean = true) =>
    `${API_BASE_URL}/api/edit-session/${sessionId}/export?include_metadata=${includeMetadata}`,
  editSessionImport: `${API_BASE_URL}/api/edit-session/import`,
  sessionsExportMultiple: `${API_BASE_URL}/api/sessions/export-multiple`,
  sessionsBackup: `${API_BASE_URL}/api/sessions/backup`,
  sessionsList: `${API_BASE_URL}/api/sessions/list`,

  // WebSocket
  wsProcessing: (sessionId: string) => `${WS_BASE_URL}/ws/${sessionId}`,
  wsEdit: (sessionId: string) => `${WS_BASE_URL}/ws/edit/${sessionId}`,

  // Health check
  health: `${API_BASE_URL}/health`,

  // Alignment Improvement
  alignmentImprovement: {
    analyze: `${API_BASE_URL}/api/alignment-improvement/analyze`,
    status: (jobId: string) => `${API_BASE_URL}/api/alignment-improvement/status/${jobId}`,
  },

  // Document Processing
  extractDocument: `${API_BASE_URL}/api/extract-document`,

  // Auto Preprocessing
  preprocessingAuto: `${API_BASE_URL}/api/auto-preprocessing`,

  // Models
  models: `${API_BASE_URL}/api/models`,
  modelsByProvider: (provider: string) => `${API_BASE_URL}/api/models/${provider}`,

  // Ground Truth
  groundTruth: {
    info: `${API_BASE_URL}/api/ground-truth/info`,
    savedSessions: `${API_BASE_URL}/api/ground-truth/saved-sessions`,
    session: (sessionId: string) => `${API_BASE_URL}/api/ground-truth/session/${sessionId}`,
    debug: (sessionId: string) => `${API_BASE_URL}/api/debug/ground-truth/${sessionId}`,
  },

  // Configuration
  config: {
    autoSegmentation: `${API_BASE_URL}/api/config/auto-segmentation`,
  },

  // Alignment
  alignment: {
    run: `${API_BASE_URL}/api/alignment/run`,
    consensus: `${API_BASE_URL}/api/alignment/consensus`,
    status: (jobId: string) => `${API_BASE_URL}/api/alignment/status/${jobId}`,
    incrementalPlan: `${API_BASE_URL}/api/alignment/incremental-plan`,
    // Anchored window re-alignment (Option B)
    window: `${API_BASE_URL}/api/alignment/window`,
  },

  // Witness Queue
  witnessQueue: {
    start: `${API_BASE_URL}/api/witness-queue/start`,
  },

  // Processing Status
  processingStatus: (sessionId: string) => `${API_BASE_URL}/api/processing/status/${sessionId}`,

  // Witness Operations
  witness: {
    mergeUnits: (witnessId: string) => `${API_BASE_URL}/api/witness/${witnessId}/merge-units`,
    splitUnit: (witnessId: string) => `${API_BASE_URL}/api/witness/${witnessId}/split-unit`,
    deleteUnit: (witnessId: string) => `${API_BASE_URL}/api/witness/${witnessId}/delete-unit`,
    updateFromSession: (witnessId: string, sessionId: string) => `${API_BASE_URL}/api/witness/${witnessId}/update-from-session/${sessionId}`,
  },

  // Matrix Save Operation Tracking
  matrixSave: {
    start: `${API_BASE_URL}/api/matrix-save/start`,
    complete: (operationId: string) => `${API_BASE_URL}/api/matrix-save/${operationId}/complete`,
  },

  // Matrix Sessions (Save/Load/List)
  matrixSession: {
    create: `${API_BASE_URL}/api/matrix/session`,
    update: (id: string) => `${API_BASE_URL}/api/matrix/session/${id}`,
    get: (id: string) => `${API_BASE_URL}/api/matrix/session/${id}`,
    list: `${API_BASE_URL}/api/matrix/sessions`,
  },

  // Workbench Propagation
  workbenchPropagation: {
    preview: `${API_BASE_URL}/api/workbench/propagation/preview`,
    translationBatch: `${API_BASE_URL}/api/workbench/propagation/translation-batch`,
  },

  workbenchSemanticAlignment: {
    preview: `${API_BASE_URL}/api/workbench/semantic-alignment/preview`,
  },

  workbenchCrossTextAlignment: {
    preview: `${API_BASE_URL}/api/workbench/cross-text-alignment/preview`,
    cancelRun: (runId: string) => `${API_BASE_URL}/api/workbench/cross-text-alignment/preview-runs/${encodeURIComponent(runId)}/cancel`,
  },

  workbenchDeviceAnnotations: {
    detectWindow: `${API_BASE_URL}/api/workbench/device-annotations/detect-window`,
    clusterThreads: `${API_BASE_URL}/api/workbench/device-annotations/cluster-threads`,
  },

  workbenchCharacterIndex: {
    detect: `${API_BASE_URL}/api/workbench/character-index/detect`,
  },

  sharedLedgers: {
    create: `${API_BASE_URL}/api/shared-ledgers`,
    get: (shareId: string) => `${API_BASE_URL}/api/shared-ledgers/${encodeURIComponent(shareId)}`,
    recover: (shareId: string) => `${API_BASE_URL}/api/shared-ledger-recovery/${encodeURIComponent(shareId)}`,
  },
};

export default API_ENDPOINTS;
